import type { JSX } from 'react';
import {
    Alert,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import {
    ServerRequestState,
    useServerFetch,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { OperatorAsyncLoadedSection } from '../app/components/OperatorAsyncLoadedSection.tsx';
import { ParkingaboUser } from './OperatorParkingaboUsersForm.tsx';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import { DateTime } from 'luxon';
import { ProductsAddTemplateItem } from './OperatorParkingaboUsersProductsAdd.tsx';
import { Message } from 'dg-web-shared/lib/Localized.ts';
import {
    determineProductStatus,
    OperatorProductStatusIcon,
} from '../products/OperatorProductState.tsx';
import { Outlet, useNavigate, useOutletContext } from 'react-router';
import { OperatorProductDetailModal } from '../products/OperatorProductDetailModal.tsx';
import { useOperatorContext } from '../app/components/BaseLayoutAndData.tsx';
import { useQuery } from '../shared-mui-components/filter/OperatorFilterHelpers.tsx';
import {
    LastUpdateTimestampAndRefresh,
    LastUpdateTimestampAndRefreshAsyncLoadedSection,
} from '../ui/LastUpdateTimestampAndRefresh.tsx';
import {
    DetailHeader,
    DetailTabs,
    ParkingaboUserInfoTab,
    useParkingaboUserDetailOutletContext,
    HeaderBarWithRefreshButton,
} from './OperatorParkingaboUsersDetail.tsx';
import { OperatorRoutedModalContent } from '../layout/components/OperatorRoutedModalContent.tsx';
import { ResultLimitAlert } from '../shared-mui-components/ListMessages.tsx';
import { CustomerTenantState } from 'product-shared/parkingabo/CustomerTenantState.ts';
import { ParkingaboCustomerPaymentMode } from 'product-shared/parkingabo/ParkingaboOperatingMode.tsx';

export interface ParkingaboCustomerProductsResult {
    results: ParkingaboProductWithPossibleStorno[];
    eof: boolean;
}

interface ParkingaboProductWithPossibleStorno {
    contractExternalId: string;
    contractTemplateId: number;
    name: Message;
    createdAt: string;
    originalValidFrom: string;
    originalValidTo?: string;
    isStorno: boolean;
}

export function OperatorParkingaboUsersProducts() {
    const { parkingaboUser, refetchParkingaboUser } =
        useParkingaboUserDetailOutletContext();
    const [templateState] = useServerFetch<
        ProductsAddTemplateItem[],
        { roleId: string | null }
    >(
        ({ roleId }) => {
            return {
                url: `/ui-api/operator-account/parkingabo/product-templates/${roleId}`,
            };
        },
        {
            roleId: parkingaboUser.roleId,
        },
    );

    const [productsState, refetchProducts] = useServerFetch<
        ParkingaboCustomerProductsResult,
        { customerNr: string },
        null
    >(
        context => ({
            url: `/ui-api/operator-account/parkingabo/customer/${context.customerNr}/products`,
        }),
        { customerNr: parkingaboUser.customerNr },
    );

    const operatorContext = useOperatorContext();
    const query = useQuery();
    const navigate = useNavigate();

    return (
        <OperatorRoutedModalContent
            header={<DetailHeader />}
            bar={
                <DetailTabs
                    parkingaboUserInfoTab={ParkingaboUserInfoTab.PRODUCT}
                    roleHasAppPayment={
                        parkingaboUser.role.paymentMode ===
                        ParkingaboCustomerPaymentMode.APP_PAYMENT
                    }
                />
            }
            style={{ padding: '0px 0px' }}
            body={
                <OperatorAsyncLoadedSection
                    requestState={templateState}
                    render={productTemplates => {
                        const purchasableProductTemplates =
                            productTemplates.filter(template =>
                                operatorContext.currentLogin.allowedProductTypes.includes(
                                    template.contractTemplateId,
                                ),
                            );
                        return (
                            <>
                                <HeaderBarWithRefreshButton
                                    button={
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={() =>
                                                navigate(`add?${query}`)
                                            }
                                            disabled={
                                                parkingaboUser.state !==
                                                    CustomerTenantState.ACTIVE ||
                                                purchasableProductTemplates.length <
                                                    1
                                            }
                                        >
                                            <Localized
                                                de="Hinzufügen"
                                                fr="Ajouter"
                                                it="Aggiungi"
                                                en="Add"
                                            />
                                        </Button>
                                    }
                                    refreshButton={
                                        <LastUpdateTimestampAndRefreshAsyncLoadedSection
                                            requestState={productsState}
                                            onRefresh={refetchProducts}
                                            render={(products, timestamp) => (
                                                <LastUpdateTimestampAndRefresh
                                                    resultsCount={
                                                        products.results.length
                                                    }
                                                    eof={products.eof}
                                                    onRefresh={refetchProducts}
                                                    timestamp={timestamp}
                                                />
                                            )}
                                        />
                                    }
                                />
                                <OperatorParkingaboUsersProductsTable
                                    productsState={productsState}
                                    goToEditProduct={(
                                        contractExternalId: string,
                                    ) =>
                                        navigate(
                                            `${contractExternalId}/detail?${query}`,
                                        )
                                    }
                                />

                                <Outlet
                                    context={{
                                        parkingaboUser: parkingaboUser,
                                        purchasableProductTemplates:
                                            purchasableProductTemplates,
                                        refetchProducts: refetchProducts,
                                        refetchParkingaboUser:
                                            refetchParkingaboUser,
                                    }}
                                />
                            </>
                        );
                    }}
                />
            }
        />
    );
}

export type ParkingaboUsersProductsAddContextType = {
    parkingaboUser: ParkingaboUser;
    purchasableProductTemplates: ProductsAddTemplateItem[];
    refetchProducts: () => void;
    refetchParkingaboUser: () => void;
};

export function OperatorParkingaboUserProductDetail() {
    const { refetchProducts } =
        useOutletContext<ParkingaboUsersProductsAddContextType>();
    const query = useQuery();

    return (
        <OperatorProductDetailModal
            backUrl={`..?${query}`}
            onProductChange={refetchProducts}
        />
    );
}

interface OperatorParkingaboUsersProductsTableProps {
    productsState: ServerRequestState<ParkingaboCustomerProductsResult, null>;
    goToEditProduct: (contractExternalId: string) => void;
}

function OperatorParkingaboUsersProductsTable({
    productsState,
    goToEditProduct,
}: OperatorParkingaboUsersProductsTableProps): JSX.Element {
    return (
        <OperatorAsyncLoadedSection
            requestState={productsState}
            render={products => {
                if (products.results.length <= 0) {
                    return (
                        <Alert severity="info">
                            <Localized
                                de="Dieser Benutzer hat keine Produkte."
                                fr="Cet utilisateur n'a pas de produits."
                                it="Questo utente non ha prodotti."
                                en="This user has no products."
                            />
                        </Alert>
                    );
                }

                return (
                    <TableContainer
                        sx={{
                            flex: '1 1 0',
                        }}
                    >
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        <Localized
                                            de="Zustand"
                                            fr="État"
                                            it="Stato"
                                            en="State"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Localized
                                            de="Beschreibung"
                                            fr="Description"
                                            it="Descrizione"
                                            en="Description"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Localized
                                            de="Beginn"
                                            fr="Début"
                                            it="Inizio"
                                            en="Begin"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Localized
                                            de="Ende"
                                            fr="Fin"
                                            it="Fine"
                                            en="End"
                                        />
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {products.results.map(product => (
                                    <TableRow
                                        key={product.contractExternalId}
                                        hover
                                        sx={{ cursor: 'pointer' }}
                                        onClick={() =>
                                            goToEditProduct(
                                                product.contractExternalId,
                                            )
                                        }
                                    >
                                        <TableCell>
                                            <OperatorProductStatusIcon
                                                status={determineProductStatus(
                                                    product.isStorno,
                                                    DateTime.fromISO(
                                                        product.originalValidFrom,
                                                    ),
                                                    product.originalValidTo
                                                        ? DateTime.fromISO(
                                                              product.originalValidTo,
                                                          )
                                                        : null,
                                                )}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Localized {...product.name} />
                                        </TableCell>
                                        <TableCell>
                                            {DateTime.fromISO(
                                                product.originalValidFrom,
                                            ).toFormat('dd.MM.yyyy – HH:mm')}
                                        </TableCell>
                                        <TableCell>
                                            {product.originalValidTo
                                                ? DateTime.fromISO(
                                                      product.originalValidTo,
                                                  ).toFormat(
                                                      'dd.MM.yyyy – HH:mm',
                                                  )
                                                : ' - '}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <ResultLimitAlert
                            eof={products.eof}
                            numberOfResults={products.results.length}
                            hasExport={false}
                        />
                    </TableContainer>
                );
            }}
        />
    );
}
